<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            class="statement-summary-grid"
            :columnDefs="columnDefs"
            :rowData="summaryData ? summaryData.owner_properties : []"
            :getRowClass="getRowClass"
            :getRowStyle="getRowStyle"
            :header-height="50"
            :loading="!summaryData || !isChildrenFetched"
            :detailCellRendererParams="{ columnSettings: columnSettings }"
            overlayLoadingTemplate="<span class='ag-overlay-loading-center'>Statement generation can take up to one minute, please be patient...</span>" 
            :master-detail="true"
            :detailCellRenderer="detailCellRenderer"
            :onCellClicked="onCellClicked"
            :isSearch="false"
            :isGroupOpenByDefault="() => true"
            :getRowId="
                (params) =>
                    params.data && params.data.property_id ? params.data.property_id.toString() : 'totals-row'
            "
            :pinnedBottomRowData="summaryData ? summaryData.totals : []"
            grid-theme="ag-theme-alpine-owner-statement"
        >
            <responsive-button
                :icon="['far', 'file-pdf']"
                variant="primary"
                @click="handleDownloadPDF"
                :busy="pdfBusy"
                >Download PDF</responsive-button
            >
            <responsive-button
                :icon="['fas', 'file-excel']"
                variant="primary"
                @click="handleDownloadExcel"
                :busy="excelBusy"
                >Download Excel</responsive-button
            >
        </grid>
    </page>
</template>

<script>
    import API from '../../api';
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import parseISO from 'date-fns/parseISO';
    import grid from '../generic/grid/grid.vue';
    import Page from '../generic/page/page.vue';
    import { OWNER_STATEMENT_GENERATION_ENDPOINT, OWNER_STATEMENT_SETTINGS_ENDPOINT } from '../../endpoints';
    import AdditionalTablesDetailCellRenderer from './additional-tables-detail-cell-renderer.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import { convertBlobResponseToJson, getFilenameFromResponse, formatResponseError } from '../../utils/api';
    import { saveAs } from 'file-saver';

    export default {
        name: 'OwnerStatementGenerationSummary',
        components: {
            grid,
            Page,
            AdditionalTablesDetailCellRenderer,
            ResponsiveButton,
        },
        data() {
            return {
                busy: false,
                settings: null,
                summaryData: null,
                isChildrenFetched: true,
                detailCellRenderer: 'AdditionalTablesDetailCellRenderer',
                pdfBusy: false,
                excelBusy: false,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            tabs() {
                return [
                    { title: 'Generation', active: false, route: '/app/owner-statement/list' },
                    { title: `Summary: ${this.summaryData ? this.summaryPeriod : ''}`, active: true },
                ];
            },
            columnSettings() {
                return this.settings?.reservations_table_columns || [];
            },
            fromDate() {
                return this.$route.query.from_date;
            },
            toDate() {
                return this.$route.query.to_date;
            },
            generationIds() {
                return this.$route.query.generation_ids;
            },
            ownerIds() {
                return this.$route.query.owner_ids;
            },
            propertyIds() {
                return this.$route.query.property_ids;
            },
            summaryPeriod() {
                const formattedFrom = format(parseISO(this.summaryData.from_date), this.userUnicodeDateFormat);
                const formattedTo = format(parseISO(this.summaryData.to_date), this.userUnicodeDateFormat);
                return `${formattedFrom} - ${formattedTo}`;
            },
            bodyParams() {
                return {
                    from_date: this.fromDate,
                    to_date: this.toDate,
                    generation_ids: this.generationIds ? this.generationIds.split(',') : [],
                    owner_ids: this.ownerIds ? this.ownerIds.split(',') : [],
                    property_ids: this.propertyIds ? this.propertyIds.split(',') : [],
                };
            },
            columnDefs() {
                return [
                    {
                        field: 'property_id',
                        cellRenderer: 'agGroupCellRenderer',
                        headerName: '',
                        cellRendererParams: {
                            innerRenderer: ({ data }) => {
                                if (!data || !data.property_id) return 'Totals';
                                return '';
                            },
                        },
                        minWidth: 50,
                        maxWidth: 50,
                        cellStyle: (params) => {
                            if (!params.data || !params.data.property_id) {
                                return { minWidth: '150px', maxWidth: '150px' };
                            }
                            return { minWidth: '50px', maxWidth: '50px' };
                        },
                    },
                    {
                        field: 'owner_name',
                        headerName: 'Owner',
                        sortable: true,
                    },
                    {
                        field: 'property_name',
                        headerName: 'Property',
                        sortable: true,
                        minWidth: 320,
                    },
                    {
                        headerName: 'Gross Rental Revenue',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_rental_price'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Management Fees',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_fee', true),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Management Commission',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_commission', true),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Channel Commission',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_channel_commission', true),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Owner Tax on Fees',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_hst_tax_fees', true),
                        hide: this.isColumnHidden('total_hst_tax_fees'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Owner Tax on Commissions',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_hst_tax_commissions', true),
                        hide: this.isColumnHidden('total_hst_tax_commissions'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Work Orders',
                        valueGetter: (params) =>
                            this.getAmountColumnValue(params, 'total_amount_work_orders', false, true),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Rental Expenses',
                        valueGetter: (params) =>
                            this.getAmountColumnValue(params, 'total_amount_rental_expenses', false, true),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                    {
                        headerName: 'Non Commissionable Fees and Services',
                        valueGetter: (params) =>
                            this.getAmountColumnValue(params, 'total_non_commissionable_fees_and_services'),
                        hide: this.isColumnHidden('total_non_commissionable_fees_and_services'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 160,
                        sortable: true,
                    },
                    {
                        headerName: 'Total Balance Due Owner',
                        valueGetter: (params) => this.getAmountColumnValue(params, 'total_balance_due_owner'),
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 110,
                        sortable: true,
                    },
                ];
            },
        },
        mounted() {
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.onMount();
        },
        methods: {
            async onMount() {
                this.busy = true;
                try {
                    await Promise.all([this.generateSummary(), this.fetchSettings()]);
                } catch {
                } finally {
                    this.busy = false;
                }
            },
            onCellClicked(event) {
                if (event.node.master) {
                    event.node.setExpanded(!event.node.expanded);
                }
            },
            async fetchSettings() {
                try {
                    const { data } = await API.get(OWNER_STATEMENT_SETTINGS_ENDPOINT());
                    this.settings = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch settings.' });
                }
            },
            async generateSummary() {
                try {
                    const { data } = await API.post(
                        OWNER_STATEMENT_GENERATION_ENDPOINT() + 'generate-summary/',
                        this.bodyParams
                    );
                    this.summaryData = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not generate summary.' });
                    throw e;
                }
            },
            getAmountColumnValue(params, fieldName, is_negative, invert_sign = false) {
                let value;
                if (!params.data || !params.data.property_id) {
                    value = params.data[fieldName];
                } else {
                    value = params.data.totals[0]?.[fieldName];
                }
                if (invert_sign) {
                    return value * -1;
                }
                return is_negative ? `-${value}` : value;
            },
            isColumnHidden(column) {
                return this.summaryData && !this.summaryData.meta_data.active_columns[column];
            },
            getRowStyle(params) {
                if (params.node.rowPinned === 'bottom') {
                    return { 'font-weight': 'bold' };
                }
            },
            getRowClass({ data }) {
                if (data.owner_name) {
                    return 'lx-owner-statement-row';
                }
                return '';
            },
            async handleDownloadPDF() {
                try {
                    this.pdfBusy = true;
                    const response = await API.post(
                        OWNER_STATEMENT_GENERATION_ENDPOINT() + 'summary-pdf/',
                        this.bodyParams,
                        { responseType: 'blob' }
                    );
                    saveAs(response.data, getFilenameFromResponse(response));
                } catch (e) {
                    if (e.response) {
                        e.response.data = await convertBlobResponseToJson(e.response);
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not download owner statement summary pdf.' });
                } finally {
                    this.pdfBusy = false;
                }
            },
            async handleDownloadExcel() {
                try {
                    this.excelBusy = true;
                    const response = await API.post(
                        OWNER_STATEMENT_GENERATION_ENDPOINT() + 'summary-excel/',
                        this.bodyParams,
                        { responseType: 'blob' }
                    );
                    saveAs(response.data, getFilenameFromResponse(response));
                } catch (e) {
                    if (e.response) {
                        e.response.data = await convertBlobResponseToJson(e.response);
                    }
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not download owner statement summary excel.' });
                } finally {
                    this.excelBusy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .statement-summary-grid {
        :deep(.ag-row) {
            overflow: visible !important;
            user-select: none;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
        }
    }
</style>
